import { Box } from "@mui/material";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { getDefaultBoardMatHex } from "./boardMatPalette";
import { getBoardMatSurfaceLayers } from "./boardMatSurface";
import JigsawDieLineOverlay from "./JigsawDieLineOverlay";
import { BOARD_SIZE, PieceVisual, SVG_SIZE } from "./PieceVisual";

/**
 * Static play board for app-store screenshots: mat texture, die lines, and placed pieces.
 */
export default function ScreenshotBoard({
    grid,
    imageUrl,
    paths,
    boardPieceIndices = [],
    /**
     * Optional free placement (overrides home cells).
     * `{ pieceIndex, x, y, rotateDeg? }[]` in board px (BOARD_SIZE space).
     */
    boardPiecePlacements = null,
    svgPathFallback,
    matPreset = null,
    matColor = getDefaultBoardMatHex(),
    grooveStyle = null,
    onScaleChange,
    fullBleed = false,
    boardInnerRef = null,
    showDieLines = true,
    pieceBevel3d = true,
    showFullImage = false,
    fullImageFit = "slice",
    fillHeight = false,
    /** Scale board to cover the container (object-fit: cover) — no letterbox gaps. */
    coverViewport = false,
    imageDimOpacity = 0,
    /** pieceIndex → one-shot lock-shine key (same overlay as live play). */
    lockShineByPieceIndex = null,
}) {
    const fullImagePreserveAspectRatio =
        fullImageFit === "meet"
            ? "xMidYMid meet"
            : fullImageFit === "cover"
              ? "xMidYMid slice"
              : "xMinYMin slice";
    const containerRef = useRef(null);
    const [scale, setScale] = useState(1);

    const matSurface = useMemo(
        () => getBoardMatSurfaceLayers(matPreset ?? { hex: matColor, surface: "matte" }),
        [matPreset, matColor],
    );

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;

        const apply = () => {
            const w = el.clientWidth;
            const h = el.clientHeight;
            let nextScale = 1;
            if (coverViewport && w > 0 && h > 0) {
                nextScale = Math.max(w, h) / BOARD_SIZE;
            } else if (w > 0) {
                nextScale = w / BOARD_SIZE;
            }
            setScale((prev) => (prev === nextScale ? prev : nextScale));
            onScaleChange?.(nextScale);
        };

        apply();
        const observer = new ResizeObserver(apply);
        observer.observe(el);
        return () => observer.disconnect();
    }, [onScaleChange, coverViewport]);

    const fill = fillHeight || coverViewport;

    return (
        <Box
            sx={{
                width: "100%",
                height: fill ? "100%" : undefined,
                maxWidth: "100%",
                mx: "auto",
                boxSizing: "border-box",
                px: 0,
            }}
        >
            <Box
                ref={containerRef}
                sx={{
                    width: "100%",
                    height: fill ? "100%" : undefined,
                    maxWidth: "100%",
                    aspectRatio: fill ? undefined : "1 / 1",
                    position: "relative",
                    overflow: fullBleed || coverViewport ? "hidden" : "visible",
                }}
            >
                <Box
                    ref={boardInnerRef}
                    sx={{
                        position: "absolute",
                        ...(coverViewport
                            ? {
                                  top: "50%",
                                  left: "50%",
                                  transform: `translate(-50%, -50%) scale(${scale})`,
                                  transformOrigin: "center center",
                              }
                            : {
                                  top: 0,
                                  left: "50%",
                                  transform: `translateX(-50%) scale(${scale})`,
                                  transformOrigin: "top center",
                              }),
                        width: BOARD_SIZE,
                        height: BOARD_SIZE,
                        borderRadius: fullBleed || coverViewport ? 0 : 2,
                        overflow: "visible",
                        border: fullBleed || coverViewport ? "none" : `3px solid ${matPreset?.hex ?? matColor}`,
                        boxSizing: "border-box",
                        isolation: "isolate",
                        ...matSurface.baseSx,
                    }}
                >
                    <Box aria-hidden sx={matSurface.textureSx} />
                    {showFullImage && imageUrl ? (
                        <>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
                                width={BOARD_SIZE}
                                height={BOARD_SIZE}
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    display: "block",
                                    zIndex: 2,
                                }}
                            >
                                <image
                                    href={imageUrl}
                                    x="0"
                                    y="0"
                                    width={SVG_SIZE}
                                    height={SVG_SIZE}
                                    preserveAspectRatio={fullImagePreserveAspectRatio}
                                />
                            </svg>
                            {imageDimOpacity > 0 ? (
                                <Box
                                    aria-hidden
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: BOARD_SIZE,
                                        height: BOARD_SIZE,
                                        bgcolor: `rgba(0, 0, 0, ${imageDimOpacity})`,
                                        zIndex: 3,
                                        pointerEvents: "none",
                                    }}
                                />
                            ) : null}
                            {showDieLines ? (
                                <JigsawDieLineOverlay
                                    pieces={paths}
                                    opacity={0.72}
                                    svgPathFallback={svgPathFallback}
                                    engravedGroove
                                    strokeWidth={0.55}
                                    grooveShadowOpacity={grooveStyle?.shadowOpacity}
                                    grooveHighlightOpacity={grooveStyle?.highlightOpacity}
                                    grooveStroke={grooveStyle?.stroke}
                                    zIndex={4}
                                    preserveAspectRatio={fullImagePreserveAspectRatio}
                                />
                            ) : null}
                        </>
                    ) : (
                        <>
                            {showDieLines ? (
                                <JigsawDieLineOverlay
                                    pieces={paths}
                                    opacity={0.72}
                                    svgPathFallback={svgPathFallback}
                                    engravedGroove
                                    strokeWidth={0.55}
                                    grooveShadowOpacity={grooveStyle?.shadowOpacity}
                                    grooveHighlightOpacity={grooveStyle?.highlightOpacity}
                                    grooveStroke={grooveStyle?.stroke}
                                />
                            ) : null}
                            {grid > 0 && imageUrl
                                ? (Array.isArray(boardPiecePlacements) &&
                                  boardPiecePlacements.length > 0
                                      ? boardPiecePlacements
                                      : boardPieceIndices.map((pieceIndex) => {
                                            const cellPx = BOARD_SIZE / grid;
                                            return {
                                                pieceIndex,
                                                x: (pieceIndex % grid) * cellPx,
                                                y: Math.floor(pieceIndex / grid) * cellPx,
                                                rotateDeg: 0,
                                            };
                                        })
                                  ).map((placement, idx) => {
                                      const pieceIndex = placement.pieceIndex;
                                      const rotateDeg = Number(placement.rotateDeg) || 0;
                                      const scattered =
                                          placement.scattered === true || rotateDeg !== 0;
                                      return (
                                          <div
                                              key={`board-piece-${pieceIndex}-${idx}`}
                                              style={{
                                                  position: "absolute",
                                                  left: placement.x,
                                                  top: placement.y,
                                                  zIndex: 1 + idx,
                                                  pointerEvents: "none",
                                                  transform: rotateDeg
                                                      ? `rotate(${rotateDeg}deg)`
                                                      : undefined,
                                                  transformOrigin: "center center",
                                                  filter: scattered
                                                      ? "drop-shadow(0 3px 5px rgba(0,0,0,0.35))"
                                                      : undefined,
                                              }}
                                          >
                                              <PieceVisual
                                                  pieceIndex={pieceIndex}
                                                  imageUrl={imageUrl}
                                                  grid={grid}
                                                  paths={paths}
                                                  anchorToCell={!scattered}
                                                  bevel3d={pieceBevel3d}
                                                  lockShineKey={lockShineByPieceIndex?.[pieceIndex] || null}
                                              />
                                          </div>
                                      );
                                  })
                                : null}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
